import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AlertTriangle, CheckCircle2, Loader2, Sparkles } from 'lucide-react';

const statusTone = {
  ready_to_apply: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  manual_review: 'border-amber-200 bg-amber-50 text-amber-700',
  skipped: 'border-slate-200 bg-slate-50 text-slate-600',
  already_complete: 'border-blue-200 bg-blue-50 text-blue-700',
};

const statusLabel = {
  ready_to_apply: 'Ready to Apply',
  manual_review: 'Manual Review',
  skipped: 'Skipped',
  already_complete: 'Already Complete',
};

const fieldLabelMap = {
  jacket_style: 'Jacket Style',
  lapel_type: 'Lapel Type',
  button_configuration: 'No of Buttons',
  pleats: 'Front Pleat Style',
  collar: 'Collar Style',
  cuff: 'Cuff Type',
  waistcoat_style: 'Front Style',
  neck_style: 'Neck Style',
  lapel_style: 'Lapel Style',
  hem_style: 'Hem Style',
};

const humanizeField = (fieldKey) => fieldLabelMap[fieldKey] || String(fieldKey || '').replace(/_/g, ' ');

const formatProfile = (profile = {}) => {
  const entries = Object.entries(profile || {}).filter(([, value]) => String(value || '').trim());
  if (entries.length === 0) {
    return '—';
  }
  return entries.map(([key, value]) => `${humanizeField(key)}: ${value}`).join(' • ');
};

const formatMissing = (fields = []) => {
  if (!fields.length) {
    return '—';
  }
  return fields.map(humanizeField).join(', ');
};

const summaryCards = (summary = {}) => [
  { key: 'ready', label: 'Ready to Apply', value: summary.ready_to_apply || 0 },
  { key: 'manual', label: 'Manual Review', value: summary.manual_review || 0 },
  { key: 'skipped', label: 'Skipped', value: summary.skipped || 0 },
  { key: 'complete', label: 'Already Complete', value: summary.already_complete || 0 },
];

export const MuslinDesignBackfillDialog = ({
  open,
  onOpenChange,
  loading = false,
  applying = false,
  previewData = null,
  scopeLabel = 'visible rows',
  onApply,
}) => {
  const items = previewData?.items || [];
  const summary = previewData?.summary || {};
  const canApply = (summary.ready_to_apply || 0) > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] max-w-7xl flex-col overflow-hidden p-0" data-testid="muslin-design-backfill-dialog">
        <DialogHeader className="border-b border-slate-200 px-6 py-4">
          <DialogTitle className="flex items-center gap-2 text-lg" data-testid="muslin-design-backfill-title">
            <Sparkles className="h-4 w-4 text-amber-600" /> Review Muslin Design Backfill
          </DialogTitle>
          <DialogDescription data-testid="muslin-design-backfill-description">
            Scans {scopeLabel} for clearly linked historical order garments, suggests missing muslin design details, and skips ambiguous cases with reasons.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="flex min-h-[280px] flex-col items-center justify-center gap-3 text-sm text-slate-500" data-testid="muslin-design-backfill-loading-state">
              <Loader2 className="h-5 w-5 animate-spin" />
              Reviewing linked muslin history…
            </div>
          ) : (
            <>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4" data-testid="muslin-design-backfill-summary-grid">
                {summaryCards(summary).map((card) => (
                  <Card key={card.key} className="border-slate-200 bg-white shadow-sm" data-testid={`muslin-design-backfill-summary-${card.key}`}>
                    <CardContent className="space-y-1 px-4 py-4">
                      <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">{card.label}</div>
                      <div className="text-2xl font-semibold text-slate-900">{card.value}</div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3 text-[11px] leading-5 text-slate-600" data-testid="muslin-design-backfill-help-text">
                Only clearly linked records are used. If multiple linked orders disagree, the muslin is flagged for manual review and is not auto-filled.
              </div>

              {items.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/80 px-4 py-10 text-center text-sm text-slate-500" data-testid="muslin-design-backfill-empty-state">
                  No muslin records were available in this review scope.
                </div>
              ) : (
                <div className="rounded-xl border border-slate-200" data-testid="muslin-design-backfill-table-wrapper">
                  <Table data-testid="muslin-design-backfill-table">
                    <TableHeader>
                      <TableRow className="sticky top-0 z-10 bg-white shadow-sm">
                        <TableHead>Muslin</TableHead>
                        <TableHead>Missing Details</TableHead>
                        <TableHead>Suggested Fill</TableHead>
                        <TableHead>Historical Source</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map((item) => (
                        <TableRow key={item.muslin_inventory_id} data-testid={`muslin-design-backfill-row-${item.muslin_inventory_id}`}>
                          <TableCell className="align-top">
                            <div className="font-semibold text-slate-900">{item.reference_code}</div>
                            <div className="text-xs capitalize text-slate-500">{item.garment_type || '—'}</div>
                            <div className="text-xs text-slate-500">{item.fitting_studio_customer_no || '—'} • {item.fitting_studio_customer_name || 'Unknown client'}</div>
                            <div className="mt-1 text-[11px] text-slate-500">Pattern {item.linked_pattern_code || '—'}</div>
                          </TableCell>
                          <TableCell className="align-top text-sm text-slate-600">
                            <div className="font-medium text-slate-900">Current Missing</div>
                            <div data-testid={`muslin-design-backfill-missing-${item.muslin_inventory_id}`}>{formatMissing(item.missing_required_fields)}</div>
                            <div className="mt-2 text-[11px] text-slate-500">Current: {formatProfile(item.current_design_profile)}</div>
                          </TableCell>
                          <TableCell className="align-top text-sm text-slate-600">
                            <div className="font-medium text-slate-900">Fillable Now</div>
                            <div data-testid={`muslin-design-backfill-fillable-${item.muslin_inventory_id}`}>{formatProfile(item.fillable_design_profile)}</div>
                            {item.remaining_missing_fields_after_fill?.length > 0 && item.status === 'ready_to_apply' && (
                              <div className="mt-2 text-[11px] text-slate-500">
                                Still missing after fill: {formatMissing(item.remaining_missing_fields_after_fill)}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="align-top text-sm text-slate-600">
                            <div className="font-medium text-slate-900">{item.source_order_number || 'No single source selected'}</div>
                            <div data-testid={`muslin-design-backfill-source-${item.muslin_inventory_id}`}>
                              {item.source_garment_no || '—'}{item.source_is_test_garment ? ' • Test garment' : ''}
                            </div>
                            {item.candidate_sources?.length > 0 && (
                              <div className="mt-2 space-y-1 text-[11px] text-slate-500">
                                {item.candidate_sources.slice(0, 3).map((source, index) => (
                                  <div key={`${item.muslin_inventory_id}-source-${index}`}>
                                    {source.order_number || '—'} • {source.garment_no || '—'} • {formatProfile(source.design_profile)}
                                  </div>
                                ))}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="align-top">
                            <Badge className={statusTone[item.status] || statusTone.skipped} data-testid={`muslin-design-backfill-status-${item.muslin_inventory_id}`}>
                              {statusLabel[item.status] || item.status}
                            </Badge>
                            <div className="mt-2 text-[11px] leading-5 text-slate-600" data-testid={`muslin-design-backfill-reason-${item.muslin_inventory_id}`}>
                              {item.reason || '—'}
                            </div>
                            {item.status === 'manual_review' && (
                              <div className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-700">
                                <AlertTriangle className="mt-0.5 h-3.5 w-3.5" /> Review before updating this muslin.
                              </div>
                            )}
                            {item.status === 'ready_to_apply' && (
                              <div className="mt-2 flex items-start gap-1.5 text-[11px] text-emerald-700">
                                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5" /> Safe to backfill missing details only.
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="border-t border-slate-200 px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="muslin-design-backfill-close-button">Close</Button>
          <Button onClick={onApply} disabled={loading || applying || !canApply} data-testid="muslin-design-backfill-apply-button">
            {applying ? 'Applying…' : `Apply Safe Suggestions (${summary.ready_to_apply || 0})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};